import React from 'react';
import Joi from 'joi-browser';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { clone } from 'lodash';
import {
  withStyles,
  FormControl,
  InputLabel,
  OutlinedInput,
  Divider,
  FormHelperText,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Icon,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@material-ui/core';
import {
  Save,
  Add,
  Remove,
  Info,
} from '@material-ui/icons';
import {
  getTenantUsers,
  addTenantUsers,
} from '@pingum/app-state-management/tenant/actions';

import { getBillingInformation } from '../../../../../../utils/pricingHelper';

import styles from './styles';

const getDefaultUser = () => ({
  passive: false,
  email: '',
  emailValid: false,
  emailError: 'Please provide a valid email address',
  firstName: '',
  firstNameValid: false,
  firstNameChanged: false,
  lastName: '',
  lastNameValid: false,
  lastNameChanged: false,
});

export const showSaveIcon
  = (emailValid, email, firstNameValid, lastNameValid, isFetching) => (
    emailValid
      && email.length > 0
      && firstNameValid
      && lastNameValid
      && !isFetching
  );

export const showLoadingIcon
  = (emailValid, email, firstNameValid, lastNameValid, isFetching) => (
    emailValid
      && email.length > 0
      && firstNameValid
      && lastNameValid
      && isFetching
  );

export class AddUsersComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = { tenantUsersToAdd: [getDefaultUser()] };

    if (this.props.tenantUsers.length === 0) {
      this.props.getTenantUsers(this.props.tenantName);
    }
  }

  saveUser = (index) => {
    const cleanedUpUser = clone(this.state.tenantUsersToAdd[index], true);

    // don't save if user already exists in org
    const userExists
     = this.props.tenantUsers.filter((tenantUser) => tenantUser.email === cleanedUpUser.email).length > 0;
    if (userExists) {
      this.setState((prevState) => {
        const updateUserList = prevState.tenantUsersToAdd;
        updateUserList[index].emailError = 'User already exists in organization';
        updateUserList[index].emailValid = false;
        return ({ tenantUsersToAdd: updateUserList });
      });
      return;
    }

    // clean up fields not used in request
    delete cleanedUpUser.emailValid;
    delete cleanedUpUser.emailError;
    delete cleanedUpUser.firstNameValid;
    delete cleanedUpUser.firstNameChanged;
    delete cleanedUpUser.lastNameValid;
    delete cleanedUpUser.lastNameChanged;
    delete cleanedUpUser.id;

    this.props.addTenantUsers(this.props.tenantName, [cleanedUpUser]).then(() => {
      this.removeUser(index);
    });
  }

  removeUser = (index) => {
    this.setState((prevState) => {
      const newList = prevState.tenantUsersToAdd;
      newList.splice(index, 1);
      return { tenantUsersToAdd: newList };
    });
  }

  handleEmailChange = (e, index) => {
    const changedValue = e.target.value.trim();

    const isValid = Joi.validate(changedValue, Joi.string().email()).error === null;
    this.setState((prevState) => {
      const updateUserList = prevState.tenantUsersToAdd;
      // if length goes from greater than 1 to 0
      // we are bluring and want to keep value
      if (updateUserList[index].email.length > 1 && changedValue.length === 0) {
        return {};
      }

      updateUserList[index].email = changedValue;
      updateUserList[index].emailError = 'Please provide a valid email address';
      updateUserList[index].emailValid = isValid;

      return { tenantUsersToAdd: updateUserList };
    });
  }

  handleFirstNameChange = (e, index) => {
    const changedValue = e.target.value;
    const isValid
      = changedValue !== null
        && changedValue !== undefined
        && changedValue.length >= 1
        && changedValue.length <= 50;

    this.setState((prevState) => {
      const updateUserList = prevState.tenantUsersToAdd;
      updateUserList[index].firstName = changedValue;
      updateUserList[index].firstNameChanged = true;
      updateUserList[index].firstNameValid = isValid;
      return { tenantUsersToAdd: updateUserList };
    });
  }

  handleLastNameChange = (e, index) => {
    const changedValue = e.target.value;
    const isValid
      = changedValue !== null
        && changedValue !== undefined
        && changedValue.length >= 1
        && changedValue.length <= 50;
    this.setState((prevState) => {
      const updateUserList = prevState.tenantUsersToAdd;
      updateUserList[index].lastName = changedValue;
      updateUserList[index].lastNameChanged = true;
      updateUserList[index].lastNameValid = isValid;
      return { tenantUsersToAdd: updateUserList };
    });
  }

  handlePassiveClick = (index) => {
    this.setState((prevState) => {
      const updateUserList = prevState.tenantUsersToAdd;
      updateUserList[index].passive = !updateUserList[index].passive;

      return { tenantUsersToAdd: updateUserList };
    });
  }

  addAnotherDefaultUser = (canAddMoreUsers) => {
    if (canAddMoreUsers) {
      this.setState((prevState) => ({
        tenantUsersToAdd: [
          ...prevState.tenantUsersToAdd,
          getDefaultUser(),
        ],
      }));
    }
  }

  render() {
    const {
      tenantUsers,
      isFetching,
      classes,
      onDoneAddingUsers,
      tenantObject,
    } = this.props;
    const billingInformation = getBillingInformation(tenantObject);
    /*
      If we are on a free trial we can add any number of users and the limit will be enforced
      when/if an org tries to purchase a plan. Otherwise enforce the user limit
      on the tenant object for paid organization.
    */
    const userLimit = billingInformation.hasValidPaidPlan
      ? tenantObject.userLimit
      : null; // null if on free trial
    const canAddMoreUsers = !userLimit || ((this.state.tenantUsersToAdd.length + tenantUsers.length) <= userLimit);
    const { tenantUsersToAdd } = this.state;

    return (
      <Paper className={classes.usersContainer} elevation={3}>
        <Typography
          variant="h3"
          gutterBottom
          style={{
            textAlign: 'center',
            marginTop: 0,
          }}
        >
          Invite your team
          <Button variant="outlined" style={{ float: 'right' }} onClick={onDoneAddingUsers}>Done</Button>
        </Typography>
        <Divider className={classes.headerContentDivider} />
        {
          (billingInformation.isOnFreeTrial && !billingInformation.hasValidPaidPlan) && (
            <Typography
              variant="subtitle1"
              gutterBottom
              color="secondary"
              style={{
                textAlign: 'center',
                marginTop: 16,
              }}
            >
              You are on a free trial. You can add any number of users while on your trial,
              but will need to pay for these users if you purchase a subscription.
            </Typography>

          )
        }
        {
          tenantUsersToAdd.map((user, index) => (
            <div key={user.id || index}>
              <form className={classes.container}>
                <FormControl
                  error={!user.emailValid && user.email.length > 0}
                  margin="normal"
                  className={classes.textField}
                  variant="outlined"
                >
                  <InputLabel htmlFor="email">Email</InputLabel>
                  <OutlinedInput
                    id="email"
                    value={user.email}
                    onChange={(e) => this.handleEmailChange(e, index)}
                    labelWidth={40}
                  />
                  <FormHelperText id="user-email-helper-text">
                    {
                        (!user.emailValid && user.email.length > 0)
                          ? user.emailError
                          : 'Required'
                      }
                  </FormHelperText>
                </FormControl>
                <FormControl
                  error={!user.firstNameValid && user.firstNameChanged}
                  margin="normal"
                  className={classes.textField}
                  variant="outlined"
                >
                  <InputLabel htmlFor="first-name">First Name</InputLabel>
                  <OutlinedInput
                    id="first-name"
                    value={user.firstName}
                    onChange={(e) => this.handleFirstNameChange(e, index)}
                    labelWidth={80}
                  />
                  <FormHelperText id="user-email-helper-text">
                    {
                        (!user.firstNameValid && user.firstNameChanged)
                          ? 'Please provide a valid first name (1 to 50 characters)'
                          : 'Required'
                      }
                  </FormHelperText>
                </FormControl>
                <FormControl
                  error={!user.lastNameValid && user.lastNameChanged}
                  margin="normal"
                  className={this.props.classes.textField}
                  variant="outlined"
                >
                  <InputLabel htmlFor="last-name">Last Name</InputLabel>
                  <OutlinedInput
                    id="last-name"
                    value={user.lastName}
                    onChange={(e) => this.handleLastNameChange(e, index)}
                    labelWidth={80}
                  />
                  <FormHelperText id="user-email-helper-text">
                    {
                        (!user.lastNameValid && user.lastNameChanged)
                          ? 'Please provide a valid last name (1 to 50 characters)'
                          : 'Required'
                      }
                  </FormHelperText>
                </FormControl>
                <FormControl
                  margin="normal"
                >
                  <FormControlLabel
                    className={classes.passiveActiveSwitch}
                    control={(
                      <Switch
                        checked={!user.passive}
                        onChange={() => this.handlePassiveClick(index)}
                      />
                    )}
                    label={`${user.passive
                      ? 'Passive'
                      : 'Active'} User`}
                  />
                </FormControl>
                <FormControl
                  margin="normal"
                  className={this.props.classes.textField}
                >
                  {
                    showLoadingIcon(
                      user.emailValid,
                      user.email,
                      user.firstNameValid,
                      user.lastNameValid,
                      isFetching,
                    )
                      ? <CircularProgress size={40} className={classes.addUserButton} color="primary" />
                      : (
                        <div>
                          <Button
                            className={classes.addUserButton}
                            style={{ textAlign: 'center' }}
                            color="primary"
                            variant="contained"
                            onClick={() => this.saveUser(index)}
                            disabled={!showSaveIcon(
                              user.emailValid,
                              user.email,
                              user.firstNameValid,
                              user.lastNameValid,
                              isFetching,
                            )}
                          >
                            <Save style={{ marginRight: 5 }} />
                            Add User
                          </Button>
                          <Tooltip title="Remove User">
                            <Button
                              className={classes.addUserButton}
                              color="default"
                              variant="outlined"
                              onClick={() => this.removeUser(index)}
                            >
                              <Remove />
                            </Button>
                          </Tooltip>
                        </div>
                      )
                  }
                  <FormHelperText id="buttons" />
                </FormControl>
              </form>
            </div>
          ))
        }

        <div className={classes.addAnotherUserButtonContainer}>
          <Button
            variant="outlined"
            color="default"
            disabled={!canAddMoreUsers}
            className={classes.addAnotherUserButton}
            onClick={() => { this.addAnotherDefaultUser(canAddMoreUsers); }}
          >
            Add Another User
            <Add className={this.props.classes.rightIcon} />
          </Button>
          {
              !canAddMoreUsers
              && (
                <Typography variant="caption" gutterBottom className={classes.description} component="div">
                  Your purchased subcription only allows for {userLimit} users.
                  Upgrade your plan from the billing page and unlock more users.
                </Typography>
              )
            }
        </div>

        <Divider className={classes.headerContentDivider} />

        <div className={this.props.classes.disclaimer}>
          <div>
            <Icon className={this.props.classes.icon}>
              <Info />
            </Icon>
          </div>
          <div className={this.props.classes.flex}>
            <p>
              Users new to Pingum will be sent an invitation email to
              confirm their new account and setup a password.
            </p>
            <p>
              Existing Pingum users will receive a welcome email and automatically
              see this org on their account.
            </p>
          </div>
        </div>
      </Paper>
    );
  }
}

AddUsersComponent.propTypes = {
  classes: PropTypes.object,
  tenantUsers: PropTypes.array,
  getTenantUsers: PropTypes.func,
  addTenantUsers: PropTypes.func,
  tenantName: PropTypes.string,
  tenantObject: PropTypes.object,
  isFetching: PropTypes.bool,
  onDoneAddingUsers: PropTypes.func,
};

AddUsersComponent.defaultProps = {
  classes: {},
  tenantUsers: [],
  tenantName: '',
  tenantObject: { stripePlan: null },
  getTenantUsers: () => {},
  addTenantUsers: () => {},
  isFetching: false,
  onDoneAddingUsers: () => {},
};

export const mapStateToProps = (state) => ({
  tenantUsers: state.tenant.tenantUsers,
  tenantObject: state.tenant.tenant,
  isFetching: state.tenant.isFetchingTenantUsers,
});

export const mapDispatchToProps = (dispatch) => ({
  getTenantUsers: (tenantName) => dispatch(getTenantUsers(tenantName)),
  addTenantUsers: (tenantName, users) => dispatch(addTenantUsers(tenantName, users)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(AddUsersComponent));
